/* The Neon Factory — chat launcher guard, 1.1.0
   ------------------------------------------------------------------
   1.1.0 (25 Sep 2026): the cart's subtotal and total, its quantity and remove
   controls, line totals and the Afterpay message beneath a price or total
   (snippets/tnf-afterpay.liquid - its logo and info icon are buttons) count
   as controls to keep clear, so the resting launcher no longer sits on the
   cart's amounts or the Afterpay line. And while the cart
   drawer is open (body.cart-sidebar-show) the launcher steps aside at every
   width: on a phone or tablet it slides off the edge (the drawer fills the
   screen - there is nowhere clear to put it), on a desktop it moves to the
   left of the drawer, so it never covers the drawer's Total, Afterpay line
   or Checkout. It returns as soon as the drawer closes.
   ------------------------------------------------------------------
   The Shopify Chat launcher is position:fixed, so as the page scrolls it
   passes over whatever happens to be underneath — including purchase
   controls such as a product card's "Choose Options". Being fixed does not
   make that acceptable, so this does two things:

     1. while the page is actually moving, the launcher is taken out of the
        way entirely (CSS slides it off the right edge and drops pointer
        events), so it covers nothing during a scroll;
     2. when scrolling settles, it is put back at the first vertical offset
        at which its box — plus a clearance margin — touches no actionable
        control. If no offset in range is completely clear, the one with the
        least overlap is used.

   Nothing here reaches into the chat's shadow DOM to restyle it. Position is
   communicated through a custom property on the host, which inherits into the
   shadow tree, and the launcher itself is matched from the stylesheet through
   the component's own exported part.

   The launcher is never hidden while the page is at rest, never hidden while
   the chat panel is open, and never hidden while it holds keyboard focus.
*/
(function () {
  'use strict';

  var MAX_WIDTH   = 1024;  // the widths that have the bottom toolbar
  var SETTLE_MS   = 170;   // quiet time before the launcher is put back
  var CLEARANCE   = 10;    // px of breathing room required around the launcher
  var TOOLBAR_H   = 62;
  var TOP_LIMIT   = 84;    // never rise past this from the top of the viewport
  var OFFSETS     = [0, -64, -128, -192, -256, -320, 64, 128];

  var CONTROLS = [
    'a.button-ATC',
    '.card-action a', '.card-action button',
    'form[action*="/cart/add"] button',
    'form[action*="/cart/add"] input[type="submit"]',
    '[name="add"]', '.btn-addToCart', '.button-addToCart',
    '.banner-action .banner-button',
    'a.button', 'button.button',
    /* 1.1.0: the cart's amounts and quantity controls, and the Afterpay message */
    '.cart-total-grandtotal', '.cart-total-subtotal', '.previewCartTotals.total', '.previewCartTotals.subTotal',
    '[data-plus-quantity-cart]', '[data-minus-quantity-cart]', '[data-cart-remove]', '.cart-item-total',
    '.tnf-ap:not([hidden])'
  ].join(',');

  var host = null, settleTimer = 0, moving = false, shift = 0, focusHeld = false;

  function chatHost() {
    if (!host || !host.isConnected) host = document.querySelector('shopify-chat');
    return host;
  }

  /* The launcher lives directly in shopify-chat's shadow root before the chat
     has ever been opened, and inside shopify-agent-runtime's shadow root after.
     Both are read-only lookups — nothing is modified in either tree. */
  function activator() {
    var h = chatHost();
    if (!h || !h.shadowRoot) return null;
    var b = h.shadowRoot.querySelector('button.activator');
    if (b) return b;
    var rt = h.shadowRoot.querySelector('shopify-agent-runtime');
    return rt && rt.shadowRoot ? rt.shadowRoot.querySelector('button.activator') : null;
  }

  function panelOpen() {
    var h = chatHost();
    if (!h || !h.shadowRoot) return false;
    var rt = h.shadowRoot.querySelector('shopify-agent-runtime');
    var root = rt && rt.shadowRoot ? rt.shadowRoot : h.shadowRoot;
    var p = root.querySelector('.panel');
    return !!(p && p.getBoundingClientRect().height > 0);
  }

  function inScope() {
    return window.innerWidth <= MAX_WIDTH;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setShift(px) {
    if (px === shift) return;
    shift = px;
    var h = chatHost();
    if (h) h.style.setProperty('--tnf-chat-shift', px + 'px');
  }

  function setMoving(on) {
    if (on === moving) return;
    moving = on;
    if (on) document.documentElement.setAttribute('data-tnf-chat', 'moving');
    else document.documentElement.removeAttribute('data-tnf-chat');
  }

  function overlapArea(box, els) {
    var total = 0;
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (r.bottom < box.top || r.top > box.bottom) continue;
      if (r.right < box.left || r.left > box.right) continue;
      var w = Math.min(r.right, box.right) - Math.max(r.left, box.left);
      var hh = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
      if (w > 0 && hh > 0) total += w * hh;
    }
    return total;
  }

  /* The launcher may be mid-transform when this runs (slid off to the right
     while the page moves, or already carrying a nudge). Measuring the raw
     rectangle would then test the wrong place, so the transform's own
     translation is read back out of it first. */
  function baseRect(el) {
    var r = el.getBoundingClientRect();
    var t = getComputedStyle(el).transform;
    var dx = 0, dy = 0, m;
    if (t && t !== 'none') {
      if ((m = t.match(/^matrix\(([^)]+)\)/))) {
        var p = m[1].split(',');
        dx = parseFloat(p[4]) || 0; dy = parseFloat(p[5]) || 0;
      } else if ((m = t.match(/^matrix3d\(([^)]+)\)/))) {
        var q = m[1].split(',');
        dx = parseFloat(q[12]) || 0; dy = parseFloat(q[13]) || 0;
      }
    }
    return { left: r.left - dx, right: r.right - dx, top: r.top - dy, bottom: r.bottom - dy, width: r.width };
  }

  function place() {
    var b = activator();
    if (!b || panelOpen()) { setShift(0); return; }

    var base = baseRect(b);
    if (!base.width) return;

    var els   = document.querySelectorAll(CONTROLS);
    var lower = TOP_LIMIT - base.top;                                   // most it may rise
    var upper = (window.innerHeight - TOOLBAR_H - 6) - base.bottom;     // most it may fall

    var best = 0, bestArea = Infinity;
    for (var i = 0; i < OFFSETS.length; i++) {
      var s = OFFSETS[i];
      if (s < lower || s > upper) continue;
      var area = overlapArea({
        left:   base.left  - CLEARANCE,
        right:  base.right + CLEARANCE,
        top:    base.top    + s - CLEARANCE,
        bottom: base.bottom + s + CLEARANCE
      }, els);
      if (area === 0) { best = s; bestArea = 0; break; }
      if (area < bestArea) { bestArea = area; best = s; }
    }
    setShift(best);
  }

  function onScroll() {
    if (!inScope() || panelOpen()) { setMoving(false); return; }
    if (!focusHeld && !reducedMotion()) setMoving(true);
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      /* order matters: the off-screen slide is released first, then the resting
         position is chosen. Both happen in one task, so nothing is painted in
         between and the launcher never flashes at the old spot. */
      setMoving(false);
      place();
    }, SETTLE_MS);
  }

  /* 1.1.0: while the cart drawer is open the launcher steps aside (see the header) */
  var DRAWER_CSS =
    '@media (max-width:1024px){html[data-tnf-chat-drawer] shopify-chat::part(activator){opacity:0 !important;pointer-events:none !important;transform:translate(78px,0) !important}}' +
    '@media (min-width:1025px){html[data-tnf-chat-drawer] shopify-chat::part(activator){transform:translateX(calc(-1 * var(--tnf-chat-drawer-w,0px))) !important;transition:transform .18s ease}}';
  function drawerWatch() {
    if (!document.getElementById('tnf-chat-drawer-css')) {
      var st = document.createElement('style'); st.id = 'tnf-chat-drawer-css'; st.textContent = DRAWER_CSS; document.head.appendChild(st);
    }
    var sync = function () {
      var open = document.body.classList.contains('cart-sidebar-show');
      var d = document.getElementById('halo-cart-sidebar'), h = chatHost();
      if (open) {
        var w = d ? Math.ceil(d.getBoundingClientRect().width) + 16 : 0;
        if (h) h.style.setProperty('--tnf-chat-drawer-w', w + 'px');
        document.documentElement.setAttribute('data-tnf-chat-drawer', '');
      } else {
        document.documentElement.removeAttribute('data-tnf-chat-drawer');
      }
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function start() {
    var h = chatHost();
    if (!h) return false;
    drawerWatch();

    /* keyboard users must never lose the control mid-scroll */
    h.addEventListener('focusin', function () { focusHeld = true; setMoving(false); });
    h.addEventListener('focusout', function () { focusHeld = false; });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      setMoving(false);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(place, SETTLE_MS);
    }, { passive: true });

    /* the widget mounts late and re-mounts after the first open/close */
    var tries = 0;
    var poll = setInterval(function () {
      if (activator()) { place(); if (++tries > 2) clearInterval(poll); }
      else if (++tries > 40) clearInterval(poll);
    }, 500);

    /* read-only diagnostic hook, so the resting position can be checked */
    window.TNFChatGuard = {
      version: '1.1.0',
      place: place,
      state: function () {
        var a = activator();
        return { shift: shift, moving: moving, panelOpen: panelOpen(),
                 rect: a ? baseRect(a) : null };
      }
    };

    return true;
  }

  function boot() {
    if (start()) return;
    var n = 0;
    var wait = setInterval(function () {
      if (start() || ++n > 40) clearInterval(wait);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
